import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { asText as asTextLocal } from "@/lib/format";
import {
  TOKEN_KEY, OUTLET_KEY,
  getStoredToken, setStoredToken, clearStoredToken,
  getStoredOutlet, setStoredOutlet, clearStoredOutlet,
  onTokenStorageChange,
} from "@/lib/authStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SessionContext = createContext(null);

// ---------- token helpers ----------
function readTokenFromUrl() {
  // hash fragment first (more common in SSO callbacks)
  let token = null;
  let source = null;
  if (window.location.hash) {
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    const hp = new URLSearchParams(hash);
    if (hp.get("token")) {
      token = hp.get("token");
      source = "hash";
    }
  }
  if (!token) {
    const qp = new URLSearchParams(window.location.search);
    if (qp.get("token")) {
      token = qp.get("token");
      source = "query";
    }
  }
  return token ? { token, source } : null;
}

function stripTokenFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete("token");
  let hashChanged = false;
  if (url.hash) {
    const hp = new URLSearchParams(url.hash.replace(/^#/, ""));
    if (hp.has("token")) {
      hp.delete("token");
      url.hash = hp.toString() ? `#${hp.toString()}` : "";
      hashChanged = true;
    }
  }
  if (url.searchParams.toString() === "" && !url.hash) {
    window.history.replaceState({}, "", url.pathname);
  } else {
    window.history.replaceState({}, "", url.toString());
  }
  return hashChanged;
}

function redirectToCrSso() {
  const cr = (process.env.REACT_APP_CR_BASE_URL || "").replace(/\/+$/, "");
  const mod = process.env.REACT_APP_CR_MODULE_KEY || "bulk_capture_console";
  const redirect = encodeURIComponent(window.location.origin + "/");
  // The backend tells the frontend where to go; we hit /sso/login-url first
  // so we don't have to hard-code CR's domain in the bundle.
  axios
    .get(`${API}/control-room/sso/login-url`)
    .then((r) => {
      const url = r.data.sso_login_url || `${cr}/api/auth/sso/login?module=${mod}`;
      window.location.href = `${url}&redirect_uri=${redirect}`;
    })
    .catch(() => {
      if (cr) {
        window.location.href = `${cr}/api/auth/sso/login?module=${mod}&redirect_uri=${redirect}`;
      }
    });
}

export function SessionProvider({ children }) {
  const [loading, setLoading] = useState(true);
  // init | authed | denied | no_outlet_access | unauthenticated | dependency_unavailable
  const [authStatus, setAuthStatus] = useState("init");
  const [errorPayload, setErrorPayload] = useState(null);
  // Reason the previous session ended — survives the token teardown so the login screen can explain itself
  // instead of showing a blank form.
  const [sessionNotice, setSessionNotice] = useState(null);
  const [me, setMe] = useState(null);
  const [outletId, setOutletIdState] = useState(() => getStoredOutlet());
  const [contextValidation, setContextValidation] = useState(null);

  // Step 1: capture token from URL on app mount.
  useEffect(() => {
    const found = readTokenFromUrl();
    if (found) {
      // Apply the same whitespace-token guard as acceptToken: a
      // malformed SSO callback URL (?token= , ?token=%20, etc.)
      // must never reach storage. Strip and validate length.
      const clean = typeof found.token === "string" ? found.token.trim() : "";
      if (clean.length >= 20) {
        setStoredToken(clean);
      }
      stripTokenFromUrl();
    }
  }, []);

  const getToken = useCallback(() => getStoredToken(), []);

  // Keep the current outletId inside a ref so `headers()` and
  // `fetchMe()` can read the *latest* value without listing outletId as
  // a dep. Reason: previously outletId flowed into fetchMe's deps →
  // /me #1 auto-populated outletId → fetchMe identity changed →
  // useEffect(fetchMe) re-fired → duplicate /me #2. Both were
  // authenticated, but the second call was pure round-trip noise.
  // Ref-based read makes fetchMe stable across the auto-select, so
  // production builds now issue exactly one /me per login.
  const outletIdRef = useRef(outletId);
  useEffect(() => { outletIdRef.current = outletId; }, [outletId]);

  const headers = useCallback(() => {
    const h = {};
    const t = getToken();
    // Only attach Authorization if there is a real, non-whitespace
    // token. A stale empty/whitespace value in sessionStorage must
    // not produce `Authorization: Bearer ` — that's exactly the
    // shape that triggers backend `token_missing` and the raw-JSON
    // screen the operator saw.
    if (typeof t === "string" && t.trim().length > 0) {
      h.Authorization = `Bearer ${t.trim()}`;
    }
    const oid = outletIdRef.current;
    if (oid) h["X-Selected-Outlet-Id"] = oid;
    return h;
  }, [getToken]);

  // In-flight /me deduper — if a call is already pending, return that
  // promise instead of firing a second one. Belt-and-braces on top of
  // the ref-based stability fix above.
  const meInFlightRef = useRef(null);

  const fetchMe = useCallback(async () => {
    if (meInFlightRef.current) return meInFlightRef.current;
    const token = getToken();
    if (!token) {
      setAuthStatus("unauthenticated");
      setLoading(false);
      return;
    }
    // Attach a per-call correlation ID so a headerless request can be
    // matched from the browser back to the backend log entry it
    // produced. Header is safe to log (opaque UUID, no secret).
    const requestId = `BC-AUTH-${
      (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
      Math.random().toString(36).slice(2) + Date.now().toString(36)
    }`;
    const hdrs = { ...headers(), "X-Request-ID": requestId };
    // Structured outbound log so we can diff browser Network against
    // backend `extract_bearer_diag` when correlating a token_missing.
    try {
      console.info(JSON.stringify({
        event: "bc_me_outbound",
        request_id: requestId,
        token_present: true,
        authorization_attached: Boolean(hdrs.Authorization),
      }));
    } catch { /* logging must never break the request */ }

    const p = (async () => {
      try {
        const r = await axios.get(`${API}/control-room/me`, { headers: hdrs });
        setMe(r.data);
        const allowed = r.data.outlet_ids || [];
        // Auto-select if there is exactly one allowed outlet.
        // MUST go through the helper so the value lands in localStorage
        // (per the auth-storage migration) — a raw sessionStorage.setItem
        // here would silently regress the persistence contract for
        // single-outlet users (tab close/reopen would lose the outlet).
        const currentOutlet = outletIdRef.current;
        if (!currentOutlet && allowed.length === 1) {
          setStoredOutlet(allowed[0]);
          setOutletIdState(allowed[0]);
        }
        // Clear stored outlet if it's no longer allowed
        if (currentOutlet && !allowed.includes(currentOutlet)) {
          clearStoredOutlet();
          setOutletIdState(null);
        }
        if (allowed.length === 0) {
          setAuthStatus("no_outlet_access");
        } else {
          setAuthStatus("authed");
        }
        setErrorPayload(null);
        setSessionNotice(null);
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail || {};
        const code = detail.error_code;
        // Defensive: backend may have nested another service's error
        // body inside `detail.message`. Force it to a string before it
        // can leak into JSX via setErrorPayload.
        const safeDetail = (detail && typeof detail === "object")
          ? { ...detail, message: asTextLocal(detail.message) }
          : detail;
        // Identity service unreachable / misconfigured / unreadable: the token may be perfectly good, so
        // it is NOT discarded. Fail closed on a dedicated screen with a bounded retry instead of pretending
        // the credentials were wrong.
        const dependencyFailure =
          code === "cr_unreachable" || code === "cr_not_configured" || code === "cr_invalid_response" ||
          (typeof code === "string" && code.startsWith("cr_http_")) ||
          (!code && (status === undefined || status >= 500));
        if (dependencyFailure) {
          setAuthStatus("dependency_unavailable");
          setErrorPayload(safeDetail && safeDetail.message
            ? safeDetail
            : { error_code: code || "identity_service_unavailable", message: e.message });
        } else if (code === "token_missing" || code === "token_expired" || code === "token_invalid" ||
                   code === "cr_unauthorized" || status === 401) {
          clearStoredToken();
          setSessionNotice({
            error_code: code || "session_invalid",
            message: code === "token_expired"
              ? "Your session has expired. Please sign in again."
              : "Your session is no longer valid. Please sign in again.",
          });
          setAuthStatus("unauthenticated");
        } else if (code === "access_denied" || code === "capability_denied" ||
                   code === "no_outlet_access" || status === 403) {
          setAuthStatus("denied");
          setErrorPayload(safeDetail);
        } else {
          setAuthStatus("denied");
          setErrorPayload(safeDetail || { error_code: "unknown", message: e.message });
        }
      } finally {
        setLoading(false);
        meInFlightRef.current = null;
      }
    })();
    meInFlightRef.current = p;
    return p;
  }, [getToken, headers]);

  const validateContext = useCallback(async () => {
    if (!outletId) {
      setContextValidation({ valid: false, error_code: "outlet_not_selected" });
      return;
    }
    try {
      const r = await axios.post(`${API}/control-room/context/validate`, {}, { headers: headers() });
      const safe = {
        ...r.data,
        message: asTextLocal(r.data?.message),
        error_code: asTextLocal(r.data?.error_code),
      };
      setContextValidation(safe);
    } catch (e) {
      setContextValidation({
        valid: false,
        error_code: "context_validate_failed",
        message: asTextLocal(e?.response?.data?.detail?.message) || e.message,
      });
    }
  }, [outletId, headers]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    if (outletId && authStatus === "authed") validateContext();
  }, [outletId, authStatus, validateContext]);

  const setOutletId = useCallback((id) => {
    if (id) setStoredOutlet(id);
    else clearStoredOutlet();
    setOutletIdState(id);
    // Outlet switch wipes the in-memory vendor-categories cache so the
    // next render fetches the new outlet's allow-list. The hook itself
    // refetches on (org_id, outlet_id) change, but clearing here removes
    // any leftover stale entries from other outlets.
    import("@/hooks/useVendorCategories").then((m) => m.clearVendorCategoriesCache())
        .catch(() => {});
  }, []);

  const signOut = useCallback(() => {
    clearStoredToken();
    clearStoredOutlet();
    setOutletIdState(null);
    setMe(null);
    setSessionNotice({ error_code: "signed_out", message: "You have been signed out." });
    setAuthStatus("unauthenticated");
  }, []);

  const acceptToken = useCallback(async (token) => {
    // Whitespace-token defense: reject anything that won't survive
    // backend `extract_bearer` parsing. JWTs are always >20 chars in
    // practice; rejecting shorter / blank values prevents the broken
    // "stored whitespace → token_missing on /me" loop reported
    // 2026-02-14 by an operator.
    if (typeof token !== "string" || token.trim().length < 20) return;
    setStoredToken(token);
    setSessionNotice(null);
    setLoading(true);
    setAuthStatus("init");
    await fetchMe();
  }, [fetchMe]);

  // Cross-tab logout sync: when another tab clears the token via
  // `clearStoredToken`, this tab must drop its in-memory user + move
  // to `unauthenticated` state so the router shows the login screen
  // instead of stale data. When another tab writes a NEW token
  // (fresh SSO in tab B), this tab re-fetches /me.
  useEffect(() => {
    return onTokenStorageChange((newValue) => {
      if (!newValue) {
        // Token gone in another tab → sync logout here.
        setMe(null);
        setAuthStatus("unauthenticated");
        setContextValidation(null);
      } else {
        // Token appeared/changed in another tab → refresh our copy.
        setLoading(true);
        setAuthStatus("init");
        fetchMe();
      }
    });
  }, [fetchMe]);

  const goToSso = useCallback(() => redirectToCrSso(), []);

  const has = useCallback(
    (cap) => Array.isArray(me?.capabilities) && me.capabilities.includes(cap),
    [me]
  );

  const value = useMemo(
    () => ({
      loading,
      authStatus,
      errorPayload,
      sessionNotice,
      me,
      user: me?.user || null,
      org: me?.org || null,
      outletIds: me?.outlet_ids || [],
      outlets: me?.outlets || (me?.outlet_ids || []).map((id) => ({ id, name: id })),
      currentOutletId: outletId,
      capabilities: me?.capabilities || [],
      has,
      contextValidation,
      setOutletId,
      refresh: fetchMe,
      validateContext,
      signOut,
      goToSso,
      acceptToken,
      getToken,
    }),
    [
      loading, authStatus, errorPayload, me, outletId,
      contextValidation, setOutletId, fetchMe, validateContext,
      signOut, goToSso, acceptToken, getToken, has, sessionNotice,
    ]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export const useSession = () => useContext(SessionContext);

export { TOKEN_KEY, OUTLET_KEY };
